import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Clock, 
  Shield, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Eye,
  MessageCircle
} from 'lucide-react';
import { apiService } from '../services/apiService';

const AdminDashboard = ({ user }) => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    disabled: 0
  });
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [comment, setComment] = useState('');

  useEffect(() => {
    loadRegistrations();
  }, [selectedStatus]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/admin/registrations', { status: selectedStatus });
      
      setRegistrations(data.registrations || []);
      setStats(data.stats || {});
      setError('');
    } catch (error) {
      console.error('등록 목록 로드 오류:', error);
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (email, action) => {
    if (!comment.trim() && action !== 'APPROVE') {
      alert('처리 사유를 입력해주세요.');
      return;
    }

    try {
      setActionLoading(true);
      const result = await apiService.post('/admin/user-management', {
        email,
        action,
        comment: comment.trim()
      });

      const actionText = {
        'APPROVE': '승인',
        'REJECT': '거부',
        'DISABLE': '비활성화',
        'ENABLE': '재활성화'
      };
      alert(`사용자 ${actionText[action] || '처리'}이 완료되었습니다.`);
      
      // 임시 비밀번호가 있는 경우 표시
      if (result.temp_password) {
        prompt('임시 비밀번호 (복사해주세요):', result.temp_password);
      }
      
      setSelectedRegistration(null);
      setComment('');
      loadRegistrations();
    } catch (error) {
      console.error('사용자 관리 오류:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: Clock, 
        label: '대기중' 
      },
      APPROVED: { 
        color: 'bg-green-100 text-green-800', 
        icon: CheckCircle, 
        label: '승인됨' 
      },
      REJECTED: { 
        color: 'bg-red-100 text-red-800', 
        icon: XCircle, 
        label: '거부됨' 
      },
      DISABLED: { 
        color: 'bg-gray-100 text-gray-800', 
        icon: AlertTriangle, 
        label: '비활성화' 
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">관리자 대시보드 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-6 w-6 mr-2 text-blue-600" />
            관리자 대시보드
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            사용자 등록 신청 관리 및 계정 상태 제어
          </p>
        </div>
      </div>

      {/* 통계 카드들 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">전체</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">대기중</dt>
                  <dd className="text-lg font-medium text-yellow-600">{stats.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserCheck className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">승인됨</dt>
                  <dd className="text-lg font-medium text-green-600">{stats.approved}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserX className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">거부됨</dt>
                  <dd className="text-lg font-medium text-red-600">{stats.rejected}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">비활성화</dt>
                  <dd className="text-lg font-medium text-gray-600">{stats.disabled}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 필터 탭 */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { key: 'ALL', label: '전체', count: stats.total },
              { key: 'PENDING', label: '대기중', count: stats.pending },
              { key: 'APPROVED', label: '승인됨', count: stats.approved },
              { key: 'REJECTED', label: '거부됨', count: stats.rejected },
              { key: 'DISABLED', label: '비활성화', count: stats.disabled }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedStatus(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  selectedStatus === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>

        {/* 등록 신청 목록 */}
        <div className="divide-y divide-gray-200">
          {error ? (
            <div className="p-6 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">오류 발생</h3>
              <p className="mt-1 text-sm text-gray-500">{error}</p>
              <button
                onClick={loadRegistrations}
                className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                다시 시도
              </button>
            </div>
          ) : registrations.length === 0 ? (
            <div className="p-6 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">등록 신청이 없습니다</h3>
              <p className="mt-1 text-sm text-gray-500">선택한 상태의 등록 신청이 없습니다.</p>
            </div>
          ) : (
            registrations.map((registration) => (
              <div 
                key={registration.email} 
                className={`p-6 hover:bg-gray-50 ${
                  registration.status === 'DISABLED' || 
                  (registration.status === 'APPROVED' && registration.enabled === false)
                    ? 'bg-red-50 border-l-4 border-red-200' 
                    : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div>
                        <p className={`text-sm font-medium ${
                          registration.status === 'DISABLED' || 
                          (registration.status === 'APPROVED' && registration.enabled === false)
                            ? 'text-gray-600' 
                            : 'text-gray-900'
                        }`}>
                          {registration.name}
                        </p>
                        <p className="text-sm text-gray-500">{registration.email}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(registration.status)}
                        {(registration.status === 'DISABLED' || 
                          (registration.status === 'APPROVED' && registration.enabled === false)) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <XCircle className="h-3 w-3 mr-1" />
                            비활성
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">소속:</span> {registration.organization}
                      </p>
                      {registration.reason && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">신청사유:</span> {registration.reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        신청일: {formatDate(registration.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedRegistration(registration)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      관리
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 사용자 관리 모달 */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">사용자 관리</h3>
              
              <div className="space-y-3 mb-6">
                <div>
                  <span className="text-sm font-medium text-gray-500">이름:</span>
                  <span className="ml-2 text-sm text-gray-900">{selectedRegistration.name}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">이메일:</span>
                  <span className="ml-2 text-sm text-gray-900">{selectedRegistration.email}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">소속:</span>
                  <span className="ml-2 text-sm text-gray-900">{selectedRegistration.organization}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">현재 상태:</span>
                  <span className="ml-2">{getStatusBadge(selectedRegistration.status)}</span>
                  {(selectedRegistration.status === 'APPROVED' || selectedRegistration.status === 'DISABLED') && (
                    <span className="ml-2">
                      {selectedRegistration.status === 'DISABLED' || selectedRegistration.enabled === false ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          비활성화됨
                        </span>
                      ) : selectedRegistration.enabled === true ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          활성화됨
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <Clock className="h-3 w-3 mr-1" />
                          상태 확인 중
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {selectedRegistration.reason && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">신청사유:</span>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedRegistration.reason}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageCircle className="h-4 w-4 inline mr-1" />
                  처리 사유
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="승인/거부/비활성화 사유를 입력하세요"
                />
              </div>

              <div className="flex space-x-3">
                {selectedRegistration.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => handleAction(selectedRegistration.email, 'APPROVE')}
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      승인
                    </button>
                    <button
                      onClick={() => handleAction(selectedRegistration.email, 'REJECT')}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      거부
                    </button>
                  </>
                )}
                
                {selectedRegistration.status === 'APPROVED' && selectedRegistration.enabled !== false && (
                  <button
                    onClick={() => handleAction(selectedRegistration.email, 'DISABLE')}
                    disabled={actionLoading}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                  >
                    비활성화
                  </button>
                )}

                {(selectedRegistration.status === 'DISABLED' || 
                  (selectedRegistration.status === 'APPROVED' && selectedRegistration.enabled === false)) && (
                  <button
                    onClick={() => handleAction(selectedRegistration.email, 'ENABLE')}
                    disabled={actionLoading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    재활성화
                  </button>
                )}

                <button
                  onClick={() => {
                    setSelectedRegistration(null);
                    setComment('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;